import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaCalendarAlt, FaEye, FaPlus } from "react-icons/fa";
import dayjs from "dayjs";
import styles from "../academicConfiguration/Academiclist.module.css";
import { CustomButton } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { useSelector } from "react-redux";

function HolidayList() {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const roleId = Number(user?.role?.id);

  // Hide add button and action column for roleId 3, 4, 6
  const hideActions = [3, 4, 6].includes(roleId);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState([]);
  const [holidayTypeSearch, setHolidayTypeSearch] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Get the current search value (prioritize column filter over global search)
  const currentSearchValue = useMemo(() => {
    return holidayTypeSearch || apiSearch;
  }, [holidayTypeSearch, apiSearch]);

  // Fetch holiday list on component mount and when dependencies change
  useEffect(() => {
    if (school_id) {
      fetchHolidayList();
    }
  }, [
    school_id,
    pagination.pageIndex,
    pagination.pageSize,
    currentSearchValue,
  ]);

  // API call to fetch holiday list
  const fetchHolidayList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post(`/school/holiday/list`, {
        id: school_id,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: currentSearchValue,
      });

      if (response?.data?.status) {
        const transformedData = response.data.data.map((item) => ({
          id: item.id,
          holiday_type: item.domain_id || "",
          from_date: item.start_date
            ? dayjs(item.start_date).format("DD/MM/YYYY")
            : "",
          to_date: item.end_date
            ? dayjs(item.end_date).format("DD/MM/YYYY")
            : "",
          no_of_days: item.no_of_days || 0,
          description: item.description || "",
          client_id: item.client_id,
          school_id: item.school_id,
          isActive: item.isActive,
        }));

        setData(transformedData);
        setRowCount(response.data.pagination?.total || transformedData.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching holiday list:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on search (client-side filtering as backup)
  const filteredData = useMemo(() => {
    return data;
  }, [data]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredData.slice(start, end);
  }, [filteredData, pagination.pageIndex, pagination.pageSize]);

  const handleAdd = () => {
    navigate("/user/holidayCreate");
  };

  const handleView = (row) => {
    navigate("/user/holidayCreate", {
      state: {
        ID: row.id,
        data: row,
      },
    });
  };

  // Handle column filter change
  const handleColumnFiltersChange = (updaterOrValue) => {
    const newFilters =
      typeof updaterOrValue === "function"
        ? updaterOrValue(columnFilters)
        : updaterOrValue;
    setColumnFilters(newFilters);
  };

  // Handle column filter keydown for Enter key - capture value directly from event
  const handleFilterKeyDown = (e) => {
    if (e.key === "Enter") {
      const inputValue = e.target.value || "";
      setHolidayTypeSearch(inputValue);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  };

  // Columns matching the image
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorKey: "holiday_type",
        header: translations.labels.holidayType,
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: "Search Holiday Type",
          onKeyDown: handleFilterKeyDown,
        },
      },
      {
        accessorKey: "from_date",
        header: translations.labels.fromDate,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "to_date",
        header: translations.labels.toDate,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "no_of_days",
        header: translations.labels.noOfDaysLeave,
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => (
          <span >
            {cell.getValue()}
          </span>
        ),
      },
      {
        accessorKey: "description",
        header: translations.labels.description,
        enableSorting: true,
        enableColumnFilter: false,
      },
    ];

    // Only add actions column if role is NOT 3, 4, or 6
    if (!hideActions) {
      baseColumns.push({
        id: "actions",
        header: translations.labels.actions,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title="View"
          >
            <FaEye />
          </button>
        ),
      });
    }

    return baseColumns;
  }, [hideActions]);

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaCalendarAlt />
        </div>
        <h1 className={styles.headerTitle}>{translations.labels.holiday}</h1>
      </div>

      {/* Actions - Hide ADD button for roleId 3, 4, 6 */}
      {!hideActions && (
        <div className={styles.actions}>
          <CustomButton
            type="button"
            variant="contained"
            className={styles.addButton}
            fullWidth={false}
            onClick={handleAdd}
          >
             <FaPlus style={{ marginRight: "8px" }} />
            {translations.labels.add}
          </CustomButton>
        </div>
      )}

      {/* Table */}
      <CustomMRTTable
        columns={columns}
        data={paginatedData}
        loading={loading}
        pagination={pagination}
        rowCount={filteredData.length}
        onPaginationChange={setPagination}
        searchInput={searchInput}

         placeholder={translations.labels.searchHolidayType }
        setSearchInput={setSearchInput}
        onSearchEnter={(value) => {
          setApiSearch(value);
          setHolidayTypeSearch("");
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        enableColumnFilters={true}
      />
    </div>
  );
}

export default HolidayList;
